import { FC, useState } from 'react';
import styles from './TimesSection.module.scss';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { ExpandableSelectorList, ExpandableSelectorItem } from 'components/UI/V3/ExpandableSelector/List';
import { RadioGroup } from 'components/UI/V3/Forms/RadioGroup/RadioGroup';
import { getBusinessTimeOptions } from 'utils/TimeOptionsUtils';
type TimeMode = '24hours' | 'setTimes';

// Local type for time ranges
interface TimeRange {
    from: string;
    to: string;
}

interface TimesSectionProps {
    className?: string;
    checkInTime: TimeRange;
    checkOutTime: TimeRange;
    onCheckInTimeChange: (type: 'from' | 'to', time: string) => void;
    onCheckOutTimeChange: (type: 'from' | 'to', time: string) => void;
    onTimeModeChange: (mode: TimeMode) => void;
    timeMode: TimeMode;
    checkInLabel: string;
    checkOutLabel: string;
}

const isTimeGreaterThan = (time1: string, time2: string): boolean => {
    const timeRegex = /(\d+):(\d+)\s*(AM|PM)/i;
    const match1 = time1.match(timeRegex);
    const match2 = time2.match(timeRegex);

    if (!match1 || !match2) {
        return false;
    }

    const [_, hour1, minute1, period1] = match1;
    const [__, hour2, minute2, period2] = match2;

    let hour1Num = parseInt(hour1);
    let hour2Num = parseInt(hour2);

    // Convert to 24h format
    if (period1.toUpperCase() === 'PM' && hour1Num !== 12) hour1Num += 12;
    if (period1.toUpperCase() === 'AM' && hour1Num === 12) hour1Num = 0;
    if (period2.toUpperCase() === 'PM' && hour2Num !== 12) hour2Num += 12;
    if (period2.toUpperCase() === 'AM' && hour2Num === 12) hour2Num = 0;

    // Compare hours
    if (hour1Num !== hour2Num) {
        return hour1Num > hour2Num;
    }

    // If hours are equal, compare minutes
    return parseInt(minute1) > parseInt(minute2);
};

export const TimesSection: FC<TimesSectionProps> = ({
    checkInTime,
    checkOutTime,
    onCheckInTimeChange,
    onCheckOutTimeChange,
    onTimeModeChange,
    timeMode,
    checkInLabel,
    checkOutLabel,
    className
}) => {
    const [checkInFromExpanded, setCheckInFromExpanded] = useState(false);
    const [checkInToExpanded, setCheckInToExpanded] = useState(false);
    const [checkOutFromExpanded, setCheckOutFromExpanded] = useState(false);
    const [checkOutToExpanded, setCheckOutToExpanded] = useState(false);

    const renderCheckInTimeOptions = (type: 'from' | 'to') => {
        const timeOptions = getBusinessTimeOptions(6, 22, 15, '12h').map(option => option.label);

        return timeOptions.map(time => {
            const isDisabled =
                type === 'from' ? isTimeGreaterThan(time, checkInTime.to) : isTimeGreaterThan(checkInTime.from, time);

            return (
                <ExpandableSelectorItem
                    key={time}
                    onClick={() => !isDisabled && onCheckInTimeChange(type, time)}
                    selected={type === 'from' ? checkInTime.from === time : checkInTime.to === time}
                    disabled={isDisabled}
                >
                    {time}
                </ExpandableSelectorItem>
            );
        });
    };

    const renderCheckOutTimeOptions = (type: 'from' | 'to') => {
        const timeOptions = getBusinessTimeOptions(6, 22, 15, '12h').map(option => option.label);
        return timeOptions.map(time => {
            const isDisabled =
                type === 'from' ? isTimeGreaterThan(time, checkOutTime.to) : isTimeGreaterThan(checkOutTime.from, time);

            return (
                <ExpandableSelectorItem
                    key={time}
                    onClick={() => !isDisabled && onCheckOutTimeChange(type, time)}
                    selected={type === 'from' ? checkOutTime.from === time : checkOutTime.to === time}
                    disabled={isDisabled}
                >
                    {time}
                </ExpandableSelectorItem>
            );
        });
    };

    const timeModeOptions = [
        { value: '24hours' as TimeMode, inlineLabel: '24 hours from drop off' },
        { value: 'setTimes' as TimeMode, inlineLabel: 'Set Times' }
    ];

    return (
        <div className={`${styles.timeContainer} ${className || ''}`}>
            <RadioGroup
                label="Check In & Check Out Times"
                value={timeMode}
                options={timeModeOptions}
                onChange={onTimeModeChange}
                className={styles.timeModeRadio}
            />

            {timeMode === 'setTimes' && (
                <div className={styles.timeSectionsContainer}>
                    <div className={styles.timeSection}>
                        <label className={styles.timeLabel}>
                            {checkInLabel} <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.timeRow}>
                            <div className={styles.timeField}>
                                <span className={styles.timeSubLabel}>From</span>
                                <ExpandableSelector
                                    id="checkin-from"
                                    label={checkInTime.from}
                                    expanded={checkInFromExpanded}
                                    dropdownAlign="right"
                                    onExpand={() => setCheckInFromExpanded(!checkInFromExpanded)}
                                    onClickAway={() => setCheckInFromExpanded(false)}
                                    className={styles.timeSelector}
                                >
                                    <ExpandableSelectorList>{renderCheckInTimeOptions('from')}</ExpandableSelectorList>
                                </ExpandableSelector>
                            </div>
                            <div className={styles.timeField}>
                                <span className={styles.timeSubLabel}>To</span>
                                <ExpandableSelector
                                    id="checkin-to"
                                    label={checkInTime.to}
                                    expanded={checkInToExpanded}
                                    dropdownAlign="right"
                                    onExpand={() => setCheckInToExpanded(!checkInToExpanded)}
                                    onClickAway={() => setCheckInToExpanded(false)}
                                    className={styles.timeSelector}
                                >
                                    <ExpandableSelectorList>{renderCheckInTimeOptions('to')}</ExpandableSelectorList>
                                </ExpandableSelector>
                            </div>
                        </div>
                    </div>

                    <div className={styles.timeSection}>
                        <label className={styles.timeLabel}>
                            {checkOutLabel} <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.timeRow}>
                            <div className={styles.timeField}>
                                <span className={styles.timeSubLabel}>From</span>
                                <ExpandableSelector
                                    id="checkout-from"
                                    label={checkOutTime.from}
                                    expanded={checkOutFromExpanded}
                                    dropdownAlign="right"
                                    onExpand={() => setCheckOutFromExpanded(!checkOutFromExpanded)}
                                    onClickAway={() => setCheckOutFromExpanded(false)}
                                    className={styles.timeSelector}
                                >
                                    <ExpandableSelectorList>{renderCheckOutTimeOptions('from')}</ExpandableSelectorList>
                                </ExpandableSelector>
                            </div>
                            <div className={styles.timeField}>
                                <span className={styles.timeSubLabel}>To</span>
                                <ExpandableSelector
                                    id="checkout-to"
                                    label={checkOutTime.to}
                                    expanded={checkOutToExpanded}
                                    dropdownAlign="right"
                                    onExpand={() => setCheckOutToExpanded(!checkOutToExpanded)}
                                    onClickAway={() => setCheckOutToExpanded(false)}
                                    className={styles.timeSelector}
                                >
                                    <ExpandableSelectorList>{renderCheckOutTimeOptions('to')}</ExpandableSelectorList>
                                </ExpandableSelector>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
